import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';
import sessionStorageStore from 'store/storages/sessionStorage';
import store from 'store';

import Button from '../../../../components/global/Button';
import AppDownloadBanner from '../../../../components/global/AppDownloadBanner';
import useDismissiblePersistent from '../../../../hooks/useDismissiblePersistent';
import { EVENT_INVITE_BOTTOM_APP_DOWNLOAD_BANNER } from '../../../../constants/dismissibleKeys';
import { isMobile } from '../../../../util/device-detector';
import useGiftingEventAddonPurchase from '../../../../apps/pro/components/GiftingEventAddonItemsPurchase/hooks/useGiftingEventAddonPurchase';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../constants/event-items';
import { getSpecialtyAlbums, isLiveEventDay } from '../helpers';
import { getAsset } from '../../../../util/assets';
import { STEPS } from '../constants';

const propTypes = {
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
  authenticated: PropTypes.bool,
  setStep: PropTypes.func.isRequired,
};

const SpecialtyEventUploadFooter = ({
  albumFolder,
  authenticated,
  setStep,
}) => {
  const history = useHistory();
  const location = useLocation();
  const { $web_only: isWebOnlyLink } = qs.parse(location?.search);
  const isWebOnlyLinkBool = ['1', 'true', 1, true].includes(isWebOnlyLink);

  const { eventAddonItems: giftableEventAddonItems } =
    useGiftingEventAddonPurchase({
      albumFolderId: albumFolder?.id,
      defaultEventAddonItemTypes: [
        EVENT_ADDON_ITEM_TYPE.WALDOFIED,
        EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
        EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
      ],
      excludePurchased: true,
    });

  const [waldofiedAlbum] = getSpecialtyAlbums(
    albumFolder?.children?.albums?.records,
    albumFolder?.album_subtype,
    [EVENT_ADDON_ITEM_TYPE.WALDOFIED],
  );

  // The idea here is to have a view counter that relies on the number of times
  // the banner was dismissed. So we'll be using that in localStorage + the sessionStorage
  // to reset the "visible" state.
  const appDownloadBannerPageViews = store.get(
    EVENT_INVITE_BOTTOM_APP_DOWNLOAD_BANNER,
  );
  // Since we only want to show this on the user's 1st, 3rd and 6th visits,
  // keep these values on an array. If the current view is not included in the array,
  // we'll automatically dismiss the banner.
  const appDownloadBannerVisibleOnPageView = [0, 2, 5];

  const { onDismiss, visible } = useDismissiblePersistent(
    EVENT_INVITE_BOTTOM_APP_DOWNLOAD_BANNER,
    sessionStorageStore,
  );

  const onDismissAppDownloadBanner = () => {
    onDismiss();
    store.set(
      EVENT_INVITE_BOTTOM_APP_DOWNLOAD_BANNER,
      (appDownloadBannerPageViews || 0) + 1,
    );
  };

  const appDownloadBannerVisible = visible && isMobile();

  const isLiveEventDayBool = isLiveEventDay(
    waldofiedAlbum,
    albumFolder?.event_settings,
    true,
  );

  useEffect(() => {
    if (
      visible &&
      !appDownloadBannerVisibleOnPageView.includes(
        appDownloadBannerPageViews || 0,
      )
    ) {
      onDismissAppDownloadBanner();
    }
  }, [visible]);

  return (
    <div className="mt-auto text-center">
      <Button
        btnBlock
        onClick={() => {
          if (authenticated) {
            history.push(`/app/shutterbug/folders/${albumFolder?.id}`);
          } else {
            history.push(`/galleries/view/folder/${albumFolder?.id}`);
          }
        }}
      >
        View gallery
      </Button>
      {giftableEventAddonItems?.length > 0 && !isLiveEventDayBool && (
        <Button
          btnBlock
          className="d-flex w-100 align-items-center"
          onClick={() => setStep(STEPS.GIFTING)}
        >
          <img
            src={getAsset('/gift-white.svg')}
            className="mr-1"
            alt="Gift"
            width="16px"
          />
          Wedding gifts for the couple
        </Button>
      )}
      <div className="d-flex mt-1">
        <a href="/terms" target="_blank" className="m-auto text-xs">
          Terms of Use
        </a>
      </div>
      <p className="mt-1 mb-0 text-center text-xs">
        This site is protected by reCAPTCHA.
      </p>
      {appDownloadBannerVisible && !isWebOnlyLinkBool && (
        <AppDownloadBanner onDismiss={onDismissAppDownloadBanner} />
      )}
    </div>
  );
};

SpecialtyEventUploadFooter.propTypes = propTypes;

export default SpecialtyEventUploadFooter;
